import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';

import { FairnessButtonComponent } from '@core/components/fairness-button/fairness-button.component';
import { TableDateCellComponent } from '@core/components/table/table-date-cell/table-date-cell.component';
import { capitalizeFirstLetter } from '@core/utils/capitalize-first-letter.util';
import { TableColorCellComponent } from '@core/components/table/table-color-cell/table-color-cell.component';
import { UpgraderHistory } from '../interfaces/upgrader-history.interface';
import { CostComponent } from '@core/components/cost/cost.component';
import { ModalService } from '@core/modals/services/modal.service';

interface Props {
  modalService: ModalService;
}

export const upgraderHistoryTableConfig = ({
  modalService,
}: Props): ColumnDef<UpgraderHistory>[] => [
  {
    accessorFn: (row) => row.id,
    id: 'id',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Game ID',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.amount,
    id: 'amount',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Bet Amount',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: cell.getValue() as number,
          balanceType: 'skins',
          gap: 4,
          color: 'var(--yellow)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.upgrade,
    id: 'upgrade',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Upgrade Value',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: cell.getValue() as number,
          balanceType: 'skins',
          gap: 4,
          color: cell.row.original.success ? 'var(--green)' : 'var(--red)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.type,
    id: 'type',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Type',
      };
    },
    cell: (cell) => capitalizeFirstLetter(cell.getValue() as string),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.success,
    id: 'result',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Result',
      };
    },
    cell: (cell) => {
      const success = cell.getValue() as boolean;
      return flexRenderComponent(TableColorCellComponent, {
        inputs: {
          value: success ? 'WON' : 'LOST',
          color: success ? 'var(--green)' : 'var(--red)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.winperc,
    id: 'winperc',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Percentage Needed',
      };
    },
    cell: (cell) => {
      return `${cell.getValue()}%`;
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.percentage,
    id: 'percentage',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Rolled Percentage',
      };
    },
    cell: (cell) => {
      return `${cell.getValue()}%`;
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.date,
    id: 'date',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Time',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(TableDateCellComponent, {
        inputs: {
          value: cell.getValue() as number,
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.serialid,
    id: 'serialid',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Serial ID',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    id: 'fairness',
    header: () => 'Fairness',
    cell: (cell) =>
      flexRenderComponent(FairnessButtonComponent, {
        inputs: {
          href: `/provably-fair/upgrader/${cell.row.original.id}`,
        },
        outputs: {
          handleClick: () => {
            modalService.close();
          },
        },
      }),
    enableSorting: false,
  },
];
